import React from 'react'
import {Field, FieldArray, Form, Formik} from "formik";
import * as Yup from "yup";

import {getBookingEngineTranslations} from "@assets/js/functions/helpers";
import CheckboxCustom from '../Form/CheckboxCustom';
import CountrySelector from "../CountrySelector";
import {CONSENTS_2} from '../../constants/summaryFormFields'

const translations = getBookingEngineTranslations();

const InputTooltip = ({
    field,
    form: {values, touched, errors},
    ...props
}) => {
    const isNotValid = touched[field.name] && errors[field.name]
    const show = isNotValid || values[field.name]

    return <div data-tooltip={translations['form_field-invalid']}>
        <label className={`form-label ${show && 'form-label--show'} ${isNotValid && 'form-label--invalid'}`}>
            {props.placeholder}
        </label>
        <input
            className={`form-field ${isNotValid && 'form-field--invalid'}`}
            maxLength={50}
            type="text" {...field} {...props}
        />
        {isNotValid && <span className='form-field--invalid-label'>{translations['form_field-invalid']}</span>}
    </div>
}

const RegisterSchemaDefault = Yup.object().shape({
    firstName: Yup.string()
        .required(),
    lastName: Yup.string()
        .required(),
    tel: Yup.string()
        .required(),
    email: Yup.string()
        .email()
        .required(),
    emailConfirm: Yup.string()
        .email()
        .required()
        .oneOf([Yup.ref('email'), null]),
    country: Yup.string(),
    street: Yup.string(),
    streetNumber: Yup.string(),
    city: Yup.string(),
    postalCode: Yup.string(),
})

const RegisterSchemaInvoice = Yup.object().shape({
    firstName: Yup.string()
        .required(),
    lastName: Yup.string()
        .required(),
    tel: Yup.string()
        .required(),
    email: Yup.string()
        .email()
        .required(),
    emailConfirm: Yup.string()
        .email()
        .required()
        .oneOf([Yup.ref('email'), null]),
    country: Yup.string(),
    street: Yup.string(),
    streetNumber: Yup.string(),
    city: Yup.string(),
    postalCode: Yup.string(),
    invoice_company: Yup.string()
        .required(),
    invoice_country: Yup.string()
        .required(),
    invoice_street: Yup.string()
        .required(),
    invoice_streetNumber: Yup.string()
        .required(),
    invoice_city: Yup.string()
        .required(),
    invoice_postalCode: Yup.string()
        .required(),
})

const copyAddress = (setFieldValue, values) => {
    const addressFields = ['street', 'streetNumber', 'city', 'postalCode', 'country'];

    addressFields.forEach(field => {
        setFieldValue(`invoice_${field}`, values[field]);
    });

    if (values.invoice_type === 'individual') {
        setFieldValue('invoice_company', `${values.firstName.trim()} ${values.lastName.trim()}`);
    }
};

export default React.forwardRef((props, ref) =>
    <Formik
        initialValues={props.initialValues}
        validationSchema={props.invoiceNeeded ? RegisterSchemaInvoice : RegisterSchemaDefault}
        onSubmit={values => {
            props.onSubmit(values)
        }}
        ref={ref}
    >
        {({values, errors, touched, setFieldValue}) => {
            return <Form>
                <div className="user_box user_box--register">
                    <div className="user_box__column user_box__column--left">
                        <Field
                            name="firstName"
                            placeholder={translations['placeholder-firstname'] + '*'}
                            component={InputTooltip}
                        />
                        <Field
                            name="lastName"
                            placeholder={translations['placeholder-lastname'] + '*'}
                            component={InputTooltip}
                        />
                        <Field
                            name="tel"
                            type='number'
                            placeholder={translations['placeholder-phone'] + '*'}
                            component={InputTooltip}
                        />
                        <Field
                            name="email"
                            placeholder={translations['placeholder-email'] + '*'}
                            component={InputTooltip}
                        />
                        <Field
                            name="emailConfirm"
                            placeholder={translations['placeholder-email-confirm'] + '*'}
                            component={InputTooltip}
                        />
                    </div>
                    <div className="user_box__column user_box__column--right">
                        <Field
                            name='country'
                            component={CountrySelector}
                            initialValue={props.initialValues.country}
                            onChange={countryCode => {
                                setFieldValue('country', countryCode)
                            }}
                        />
                        <Field
                            name="street"
                            placeholder={translations['placeholder-street']}
                            component={InputTooltip}
                        />
                        <div className="user_box__column--divide">
                            <Field
                                name="streetNumber"
                                placeholder={translations['placeholder-street-nr']}
                                component={InputTooltip}
                            />
                            <Field
                                name="roomNumber"
                                placeholder={translations['placeholder-room-nr']}
                                component={InputTooltip}
                            />
                        </div>
                        <div className="user_box__column--divide">
                            <Field
                                name="city"
                                placeholder={translations['placeholder-city']}
                                component={InputTooltip}
                            />
                            <Field
                                name="postalCode"
                                placeholder={translations['placeholder-postalcode']}
                                component={InputTooltip}
                            />
                        </div>
                        <Field
                            name="comments"
                            placeholder={translations['placeholder-comments']}
                            className='form-field form-field--textarea'
                            component='textarea'
                        />
                    </div>
                </div>

                <div className="before-like-header"></div>
                <h3 className="like-header">{translations['invoice']}</h3>
                <br/>

                {props.invoiceNeeded &&
                    <div>
                        <button
                            type="button"
                            className="link-button copy-btn"
                            onClick={() => copyAddress(setFieldValue, values)}
                        >
                            {translations["placeholder-copy-address"]}
                        </button>
                        <div className="user_box user_box--register">
                            <div className="user_box__column user_box__column--left">
                                <div className="invoice-types" role="group">
                                    <label>
                                        <Field
                                            type="radio"
                                            name="invoice_type"
                                            value="individual"
                                            checked={values.invoice_type === 'individual'}
                                        />
                                        {translations['invoice-type-individual']}
                                    </label>
                                    <label>
                                        <Field
                                            type="radio"
                                            name="invoice_type"
                                            value="company"
                                            checked={values.invoice_type === 'company'}
                                        />
                                        {translations['invoice-type-company']}
                                    </label>
                                </div>
                                <Field
                                    name="invoice_company"
                                    placeholder={values.invoice_type === "individual" ? translations['placeholder-first-and-last-name'] : translations['placeholder-company']}
                                    component={InputTooltip}
                                />
                                {values.invoice_type === 'company' && (
                                    <>
                                        <Field
                                            name="invoice_nip"
                                            placeholder={translations['placeholder-nip'] + '*'}
                                            component={InputTooltip}
                                        />
                                    </>
                                )}
                                <Field
                                    name='invoice_country'
                                    component={CountrySelector}
                                    initialValue={props.initialValues.country}
                                    onChange={countryCode => {
                                        setFieldValue('invoice_country', countryCode)
                                    }}
                                />
                            </div>
                            <div className="user_box__column user_box__column--right">
                                <Field
                                    name="invoice_street"
                                    placeholder={translations['placeholder-street'] + '*'}
                                    component={InputTooltip}
                                />
                                <div className="user_box__column--divide">
                                    <Field
                                        name="invoice_streetNumber"
                                        placeholder={translations['placeholder-street-nr'] + '*'}
                                        component={InputTooltip}
                                    />
                                    <Field
                                        name="invoice_roomNumber"
                                        placeholder={translations['placeholder-room-nr']}
                                        component={InputTooltip}
                                    />
                                </div>
                                <div className="user_box__column--divide">
                                    <Field
                                        name="invoice_city"
                                        placeholder={translations['placeholder-city'] + '*'}
                                        component={InputTooltip}
                                    />
                                    <Field
                                        name="invoice_postalCode"
                                        placeholder={translations['placeholder-postalcode'] + '*'}
                                        component={InputTooltip}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                }

                <small>
                    {translations['registered-user-form-1']}<br/>
                    {translations['registered-user-form-2']}
                    <a className='color-vc' href={`${location.origin}/profil/details`} target="_blank">
                        {translations['registered-user-form-3']}
                    </a>.
                </small>

                <FieldArray
                    name="consents"
                    render={arrayHelpers => (
                        <div className='user-data'>
                            <div className="user_box user_box--regulations user_box__agreements">
                                <div className="rodo_rules">
                                    <div className="user_box user_box--register">
                                        <div className="user_box__column user_box__column--left">
                                            <Field
                                                key='consent-1'
                                                name='consents'
                                                component={CheckboxCustom}
                                                className={'border-vc'}
                                                arrayHelpers={arrayHelpers}
                                                data={CONSENTS_2["REGULAMINY_1_SM_ID_4435"]}
                                            />

                                            <Field

                                                name='consents'
                                                component={CheckboxCustom}
                                                arrayHelpers={arrayHelpers}
                                                data={CONSENTS_2["ZGODA_1_VC_SM_ID_4436"]}
                                            />

                                            <Field

                                                name='consents'
                                                component={CheckboxCustom}
                                                arrayHelpers={arrayHelpers}
                                                data={CONSENTS_2["ZGODA_2_VC_SM_ID_4437"]}
                                            />


                                            <div className='rodo_rules__indent rodo_rules--small'
                                                 style={{fontSize: "0.8em", marginBottom: "0px", lineHeight: "normal"}}>
                                                {translations['consents-7-1']}
                                                <br/>
                                                {translations['consents-7-2']}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                />
            </Form>
        }}
    </Formik>
)
